// columnChart, clusteredColumnChart, hundredPercentStackedColumnChart.

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import {
  applySelectionStyle,
  baseChartOption,
  bucketKey,
  bucketKeys,
  firstFieldRef,
  isEmpty,
  makeCategoryClick,
  pbiAxisDefault, // AGENT-C: dotted gridlines + Segoe UI axis labels.
  pickColumn,
} from "./_echartsHelpers";

export function ColumnChartVisual({ spec, data, filters, onFilter }: VisualProps) {
  const empty = isEmpty(data);
  const rows = data.rows ?? [];
  const fields = spec.fields ?? {};
  const categoryKey = bucketKey(fields.Category);
  const yKeys = bucketKeys(fields.Y).length ? bucketKeys(fields.Y) : bucketKeys(fields.Values);
  const seriesKeys = bucketKeys(fields.Series);
  const stacked =
    spec.type === "hundredPercentStackedColumnChart" || seriesKeys.length > 0;
  const onClick = makeCategoryClick(
    { visualId: spec.id, spec, onFilter },
    firstFieldRef(fields.Category),
  );

  const categoryValues = categoryKey ? (pickColumn(rows, categoryKey) as string[]) : [];

  const baseOption: EChartsOption = {
    ...baseChartOption(),
    // AGENT-C: layer chart-specific axis config on top of the PBI defaults
    // (dotted gridlines, Segoe UI 11px labels, #e0dedd axis line).
    xAxis: {
      ...pbiAxisDefault,
      type: "category",
      data: categoryValues,
      axisTick: { ...pbiAxisDefault.axisTick, alignWithLabel: true },
    },
    yAxis: {
      ...pbiAxisDefault,
      type: "value",
      ...(spec.type === "hundredPercentStackedColumnChart"
        ? {
            max: 100,
            axisLabel: { ...pbiAxisDefault.axisLabel, formatter: "{value}%" },
          }
        : {}),
    },
    series: yKeys.map((k) => ({
      type: "bar",
      name: k,
      data: pickColumn(rows, k) as number[],
      stack: stacked ? "total" : undefined,
    })),
  };

  // P1-selection: dim non-selected categories when a matching filter is active.
  const categoryFieldRef = firstFieldRef(fields.Category);
  const categoryKeyFq = categoryFieldRef?.table && categoryFieldRef?.column
    ? `${categoryFieldRef.table}.${categoryFieldRef.column}`
    : categoryKey;
  const option = applySelectionStyle(baseOption, filters ?? {}, categoryKeyFq, categoryValues);

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <ReactECharts
        option={option}
        notMerge
        style={{ height: "100%", width: "100%" }}
        onEvents={{ click: onClick }}
      />
    </ChartShell>
  );
}
